import React, { ChangeEvent, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Box, Checkbox, CircularProgress, Typography } from "@mui/material"
import SBPIconNoExt from "../../../components/SBPIconNoExt"

import { RootState } from "../../../store/store"
import {
  setAmount as setAmountToStore,
  setPaymentPurpose,
} from "../../../store/slices/c2cSlice"
import { setNextStep } from "../../../store/slices/routingSlice/routingSlice"

import { RUBLE } from "../../../utils/constants"

import {
  BankCard,
  CardLabel,
  CardTopContainer,
  ChangeBank,
  ContentContainer,
  ContinueButton,
  IconContainer,
  MessageInput,
  SBPAgreementLink,
  CheckboxContainer,
  ButtonTransfer,
} from "./styled"

import AmountInput from "../../../components/AmountInput"
import IconBox from "../../../components/IconBox"
import ContactItem from "../../../components/ContactItem"
import { AmountContainer } from "../../../components/AmountContainer"

import { CreateSbpOutcomeTransferProps } from "./props"
import { PHKOString } from "../../../components/PHKOString"
import {
  clearInputAmount,
  formatCurrency,
  validateAmount,
} from "../../../components/AmountInput/utils/utils"
import { STEPS_NAME } from "../../../store/slices/routingSlice/flowsSteps"
import { useGetUserInfo } from "../../../api/hooks/useGetUserInfo"
import { formatPhoneNumber } from "../../../utils/formatters"
import { useSignAgreement } from "../../../api/hooks/useSignAgreement"
import { useAgreement } from "../../../api/hooks/useAgreement"
import { LoadingWindow } from "../../styled"

const CreateSbpOutcomeTransfer = ({ handleCreate, isLoading }: CreateSbpOutcomeTransferProps) => {
  const dispatch = useDispatch()

  const { mutate: signAgreement } = useSignAgreement()
  const { data: agreement } = useAgreement()
  const { data: userInfo } = useGetUserInfo()
  const [availableBalance, setAvailableBalance] = useState(
    userInfo?.balance.availableAmount || 0,
  )
  const [availableWithdraw, setAvailableWithdraw] = useState(
    userInfo?.availableWithdraw || 0,
  )

  const [checked, setChecked] = useState<boolean>(!agreement?.gaveAgreement)
  const needToSignAgreement = !agreement?.gaveAgreement

  const handleClick = () => {
    setChecked(!checked)
  }

  const amount = useSelector(
    (state: RootState) => state.transferForm.amount.value,
  )
  const paymentPurpose = useSelector(
    (state: RootState) => state.transferForm.paymentPurpose,
  )
  const setInputAmout = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value } = e.target
    const formattedValue = formatCurrency(value)
    dispatch(setAmountToStore(clearInputAmount(formattedValue)))
  }

  const transferTo = useSelector(
    (state: RootState) => state.transferForm.recipient,
  )
  const selectedBank = useSelector(
    (state: RootState) => state.transferForm.selectedBank,
  )
  const selectedBankId = useSelector(
    (state: RootState) => state.transferForm.selectedBank?.bankId,
  )
  const defaultBank = useSelector(
    (state: RootState) => state.transferForm.defaultBank,
  )

  const isDefaultBank = defaultBank?.bankId === selectedBankId

  const handleClickBank = () => {
    dispatch(setNextStep(STEPS_NAME.c2cPush.bankSelectionTransferC2C))
  }

  const handleClickNext = () => {
    if (!agreement?.gaveAgreement) signAgreement(true)
    dispatch(setAmountToStore(clearInputAmount(amount)))
    handleCreate()
  }

  const handlePaymentPurposeChange = (
    evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value } = evt.target
    dispatch(setPaymentPurpose(value))
  }

  const buttonDisabled =
    !amount ||
    validateAmount(amount, availableWithdraw, availableBalance).result ||
    !selectedBankId

  useEffect(() => {
    if (
      userInfo?.balance.availableAmount ||
      userInfo?.balance.availableAmount === 0
    )
      setAvailableBalance(userInfo?.balance.availableAmount)
    if (userInfo?.availableWithdraw || userInfo?.availableWithdraw === 0)
      setAvailableWithdraw(userInfo?.availableWithdraw)
  }, [userInfo])

  return (
    <ContentContainer>
      {isLoading && (
        <LoadingWindow>
          <CircularProgress />
        </LoadingWindow>
      )}
      <AmountContainer />
      <BankCard>
        <CardTopContainer>
          <CardLabel>Кому</CardLabel>
          <ChangeBank variant="text" onClick={handleClickBank}>
            Изменить
          </ChangeBank>
        </CardTopContainer>

        {!selectedBank && <IconBox />}

        {transferTo && selectedBank && (
          <ContactItem
            name={formatPhoneNumber(transferTo.phone)}
            bank={selectedBank}
            iconType="bank"
            desciption={
              isDefaultBank && selectedBank.bankId
                ? "Банк выбран получателем по умолчанию"
                : "Выберите банк"
            }
          />
        )}
      </BankCard>
      <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <AmountInput
          limit={availableWithdraw}
          balance={availableBalance}
          variant="filled"
          value={formatCurrency(amount)}
          onChange={setInputAmout}
          helperText="Без комиссии"
          placeholder="Введите сумму ₽"
          label="Сумма перевода"
          currency={RUBLE}
          inputProps={{ inputMode: "decimal" }}
          disabled={isLoading}
          currencyBox
        />
      </Box>

      <MessageInput
        placeholder="Сообщение"
        value={paymentPurpose}
        onChange={handlePaymentPurposeChange}
        disabled={isLoading}
      />
      {!agreement?.gaveAgreement && (
        <CheckboxContainer>
          <Checkbox
            onClick={() => {
              handleClick()
            }}
            checked={checked}
          />
          <Typography variant="subtitle2">
            Нажимая на кнопку «Перевести» я соглашаюсь{" "}
            <SBPAgreementLink href="/" rel="noreferrer">
              с условиями переводов по СБП
            </SBPAgreementLink>
          </Typography>
        </CheckboxContainer>
      )}
      <IconContainer>
        <SBPIconNoExt size={60} />
      </IconContainer>
      <Box sx={ButtonTransfer}>
        <ContinueButton
          variant="contained"
          fullWidth
          onClick={handleClickNext}
          disabled={buttonDisabled || (needToSignAgreement && !checked)}
        >
          Продолжить
        </ContinueButton>
        <PHKOString />
      </Box>
    </ContentContainer>
  )
}

export default CreateSbpOutcomeTransfer
